import { trackNewsletterSignup } from "@/lib/meta-pixel";
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PublicHeader } from "@/components/site/PublicHeader";
import { Button } from "@/components/ui/button";
import portrait from "@/assets/arda-event-portrait.webp.asset.json";
import coverAi from "@/assets/book-cover.webp";
import coverClimate from "@/assets/book-cover-2.webp";
import coverLoneliness from "@/assets/book-cover-3.webp";
import coverSpace from "@/assets/book-cover-space.jpg";
// PREVIEW ONLY: cropped from the approved reference mockup — replace with licensed theme photos.
import imgPlanet from "@/assets/tr-home/preview-theme-planet.jpg.asset.json";
import imgTech from "@/assets/tr-home/preview-theme-tech.jpg.asset.json";
import imgEconomy from "@/assets/tr-home/preview-theme-economy.jpg.asset.json";
import imgMeaning from "@/assets/tr-home/preview-theme-meaning.jpg.asset.json";
import { HOME_THEMES, type ThemeKey } from "./mock-data";
import type { WpArticle } from "@/lib/posts.types";
import type { ApprovedTheme } from "@/lib/themes-public";
import { themeByKey } from "@/lib/themes-public";

/**
 * TEMPORARY PLACEHOLDERS — the approved theme photographs (mountains, city
 * architecture, crowd, solitary figure by water) are not in the project yet.
 * Swap each `null` for an imported image when the assets are supplied.
 */
const IMG: Record<ThemeKey, string | null> = { planet: imgPlanet.url, tech: imgTech.url, economy: imgEconomy.url, meaning: imgMeaning.url };
const PLACEHOLDER_TINT: Record<ThemeKey, string> = {
  planet: "bg-theme-planet/15",
  tech: "bg-theme-tech/15",
  economy: "bg-theme-economy/15",
  meaning: "bg-theme-meaning/15",
};
const DOT: Record<ThemeKey, string> = {
  planet: "bg-theme-planet",
  tech: "bg-theme-tech",
  economy: "bg-theme-economy",
  meaning: "bg-theme-meaning",
};
const ARROW: Record<ThemeKey, string> = {
  planet: "text-theme-planet",
  tech: "text-theme-tech",
  economy: "text-theme-economy",
  meaning: "text-theme-meaning",
};

/** Newest publication first, left to right. Dates match `books` in src/routes/kitaplar.index.tsx. */
const BOOKS = [
  { title: "Hayatı Yaşanır Kılan, Boşluktur", year: "2026", cover: coverSpace }, // 2026-09-21
  { title: "Yalnızlık A.Ş.", year: "2026", cover: coverLoneliness }, // 2026-01-01
  { title: "Yapay Zekâ: Gücün İki Yüzü", year: "2024", cover: coverAi }, // 2024-01-01
  { title: "İklim Değil, İnsanlık Krizi", year: "2023", cover: coverClimate }, // 2023-01-01
];

/*
 * One composition per breakpoint: circles, labels, curves and nodes share a
 * single viewBox. Desktop 810x525 and mobile 280x290 are traced from the
 * approved reference; positions below are viewBox units.
 */
type Pt = { x: number; y: number; r: number };
const D_W = 810, D_H = 525, M_W = 280, M_H = 290;
const D_CIRCLE: Record<ThemeKey, Pt> = {
  planet: { x: 146, y: 132, r: 76 }, tech: { x: 513, y: 122, r: 70 },
  economy: { x: 115, y: 381, r: 74 }, meaning: { x: 515, y: 384, r: 78 },
};
const D_LABEL: Record<ThemeKey, { x: number; y: number; w: number }> = {
  planet: { x: 242, y: 70, w: 190 }, tech: { x: 595, y: 64, w: 205 },
  economy: { x: 205, y: 336, w: 205 }, meaning: { x: 608, y: 324, w: 200 },
};
const M_CIRCLE: Record<ThemeKey, Pt> = {
  planet: { x: 76, y: 56, r: 39 }, tech: { x: 201, y: 56, r: 40 },
  economy: { x: 69, y: 195, r: 41 }, meaning: { x: 206, y: 194, r: 42 },
};
const pct = (v: number, t: number) => `${(v / t) * 100}%`;

const D_PATHS = [
  "M34 65 C70 20 130 0 180 10 C230 20 240 120 215 180 C200 220 160 240 130 279",
  "M130 279 C100 320 60 350 40 390 C25 420 15 425 12 430",
  "M130 279 C220 250 310 250 392 298",
  "M130 279 C200 320 300 330 392 298",
  "M146 208 C140 260 180 310 260 315 C320 318 360 310 392 298",
  "M455 95 C480 140 470 230 392 298",
  "M392 298 C430 270 460 240 488 229",
  "M488 229 C540 250 590 280 637 300",
  "M637 300 C590 285 545 290 520 306",
  "M12 430 C60 495 160 535 270 528 C360 522 420 500 440 400",
];
const D_NODES: Array<[number, number, string]> = [
  [34, 65, "fill-theme-planet"], [130, 279, "fill-theme-economy"], [12, 430, "fill-theme-economy"],
  [392, 298, "fill-theme-tech"], [488, 229, "fill-theme-tech"], [637, 300, "fill-theme-meaning"],
];
const M_PATHS = [
  "M38 11 C30 18 26 24 26 31 C30 40 34 45 40 48",
  "M115 60 C125 70 132 74 140 76 C150 74 156 70 161 66",
  "M140 76 C141 88 141 100 141 110",
  "M141 110 C120 125 115 160 140 170 C165 180 185 150 165 125 C155 112 145 110 141 110",
  "M96 142 C115 140 130 145 150 150",
  "M241 56 C246 55 250 54 253 54",
  "M170 160 C185 165 195 168 206 152",
  "M122 208 C130 190 138 180 145 172",
  "M30 195 C22 197 18 198 16 198",
];
const M_NODES: Array<[number, number, string]> = [
  [38, 11, "fill-theme-planet"], [26, 31, "fill-theme-planet"], [140, 76, "fill-theme-tech"],
  [141, 110, "fill-theme-tech"], [253, 54, "fill-theme-tech"], [96, 142, "fill-theme-meaning"],
  [16, 198, "fill-theme-economy"], [122, 208, "fill-theme-economy"],
];

function Curves({ w, h, paths, nodes, r }: { w: number; h: number; paths: string[]; nodes: Array<[number, number, string]>; r: number }) {
  return (
    <svg aria-hidden viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid meet" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
      <g fill="none" className="stroke-ink/35" strokeWidth="1" strokeLinecap="round" vectorEffect="non-scaling-stroke">
        {paths.map((d) => <path key={d} d={d} vectorEffect="non-scaling-stroke" />)}
      </g>
      {nodes.map(([x, y, c]) => <circle key={`${x}-${y}`} cx={x} cy={y} r={r} className={c} />)}
    </svg>
  );
}

function circleStyle(c: Pt, W: number, H: number) {
  return { left: pct(c.x - c.r, W), top: pct(c.y - c.r, H), width: pct(c.r * 2, W) };
}

function ThemeImage({ k, eager }: { k: ThemeKey; eager: boolean }) {
  const src = IMG[k];
  return (
    <span className="relative block size-full overflow-hidden rounded-full bg-muted ring-1 ring-ink/10">
      {src ? (
        <img src={src} alt="" loading={eager ? "eager" : "lazy"} decoding="async" width={320} height={320} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      ) : (
        <span className={`grid h-full w-full place-items-center ${PLACEHOLDER_TINT[k]}`}>
          <span className="px-3 text-center text-[9px] uppercase leading-tight tracking-[0.18em] text-ink/70">Geçici<br />görsel</span>
        </span>
      )}
    </span>
  );
}

function ThemeNetwork() {
  return (
    <div className="min-w-0">
      {/* Desktop composition */}
      <div className="relative hidden w-full lg:block" style={{ aspectRatio: `${D_W} / ${D_H}` }}>
        <Curves w={D_W} h={D_H} paths={D_PATHS} nodes={D_NODES} r={6} />
        <ul className="absolute inset-0">
          {HOME_THEMES.map((t, i) => {
            const c = D_CIRCLE[t.key], l = D_LABEL[t.key];
            return (
              <li key={t.key} className="contents">
                <Link to="/tema/$slug" params={{ slug: t.slug }} tabIndex={-1} aria-hidden className="group absolute block aspect-square rounded-full" style={circleStyle(c, D_W, D_H)}>
                  <ThemeImage k={t.key} eager={i < 2} />
                </Link>
                <div className="absolute" style={{ left: pct(l.x, D_W), top: pct(l.y, D_H), width: pct(l.w, D_W) }}>
                  <span className="block text-xs tabular-nums text-ink/70">{t.no}</span>
                  <Link to="/tema/$slug" params={{ slug: t.slug }} className="mt-1 block font-serif text-[19px] font-medium leading-[1.1] text-ink hover:text-theme-tech xl:text-[22px]">
                    {t.title[0]}<br />{t.title[1]}
                  </Link>
                  <span className="mt-2 block font-serif text-[13px] leading-snug text-ink/75 xl:text-[15px]">{t.question}</span>
                  <Link to="/tema/$slug" params={{ slug: t.slug }} aria-label={`${t.title[0]} ${t.title[1]}`} className={`inline-flex size-9 items-center ${ARROW[t.key]} transition-opacity hover:opacity-70`}>
                    <ArrowRight aria-hidden className="size-5" />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      {/* Mobile composition */}
      <div className="relative mx-auto w-full max-w-[22rem] lg:hidden" style={{ aspectRatio: `${M_W} / ${M_H}` }}>
        <Curves w={M_W} h={M_H} paths={M_PATHS} nodes={M_NODES} r={3.5} />
        <ul className="absolute inset-0">
          {HOME_THEMES.map((t, i) => {
            const c = M_CIRCLE[t.key];
            return (
              <li key={t.key} className="contents">
                <Link to="/tema/$slug" params={{ slug: t.slug }} className="group absolute flex flex-col items-center" style={circleStyle(c, M_W, M_H)} aria-label={`${t.title[0]} ${t.title[1]} — temaya git`}>
                  <span className="block aspect-square w-full"><ThemeImage k={t.key} eager={i < 2} /></span>
                  <span className="mt-1.5 w-[150%] text-center font-serif text-[15px] font-medium leading-[1.15] text-ink">
                    {t.title[0]}<br />{t.title[1]}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function TrHomeRedesign({ posts = [], themes = [] }: { posts?: WpArticle[]; themes?: ApprovedTheme[] }) {
  const themeMap = new Map(themes.map((t) => [t.slug, t.primary]));
  const recent = posts.slice(0, 4).map((p) => ({ ...p, theme: themeMap.get(p.slug) ?? null }));
  return (
    <div className="tr-home-light overflow-x-clip bg-paper text-ink">
      <PublicHeader bordered={false} />

      {/* Hero + interconnected themes */}
      <section className="mx-auto grid max-w-7xl gap-5 px-5 pb-6 pt-5 sm:px-8 sm:gap-7 sm:pt-8 lg:grid-cols-[0.8fr_1.4fr] lg:gap-8 lg:pb-8 lg:pt-10">
        <div className="min-w-0 lg:pt-7">
          <h1 className="font-serif text-[44px] sm:text-[64px] lg:text-[76px] xl:text-[80px] font-medium leading-[0.95] tracking-normal">
            Fikirlerin<br />İzinde
          </h1>
          <p className="mt-3 max-w-sm font-serif text-[16px] leading-snug sm:mt-4 sm:text-[17px] text-ink/80 lg:text-[19px]">
            İnsan, teknoloji, ekonomi ve gezegenin kesişiminde daha iyi bir yarın için daha derin düşünmek.
          </p>
          <span aria-hidden className="mt-6 hidden h-px w-14 bg-ink/60 lg:block" />
        </div>
        <ThemeNetwork />
      </section>

      {/* Recent articles */}
      <section aria-labelledby="guncel" className="mx-auto max-w-7xl px-5 pb-10 sm:px-8 lg:pb-14">
        <div className="flex items-center gap-6">
          <h2 id="guncel" className="shrink-0 font-serif text-2xl font-medium lg:text-[28px]">Güncel yazılar</h2>
          <span aria-hidden className="hidden h-px flex-1 bg-ink/15 sm:block" />
          <Link to="/yazilar" className="ml-auto inline-flex min-h-11 shrink-0 items-center gap-2 font-serif text-sm text-theme-tech hover:underline sm:ml-0">
            Tüm yazılar <ArrowRight className="size-4" />
          </Link>
        </div>
        <ul className="mt-1 grid divide-y divide-ink/10 md:grid-cols-2 md:divide-y-0 lg:mt-8 lg:grid-cols-4 lg:divide-x">
          {recent.map((p) => (
            <li key={p.slug} className="lg:px-6 lg:first:pl-0 lg:last:pr-0">
              <Link to="/yazilar/$slug" params={{ slug: p.slug }} className="group grid grid-cols-[auto_minmax(0,1fr)_auto] min-h-11 gap-x-3 py-3 lg:py-0">
                <span aria-hidden className={`mt-1 size-2.5 rounded-full ${p.theme ? DOT[p.theme] : "bg-ink/30"}`} />
                <span className="min-w-0">
                  {p.theme && <span className="block text-[10px] uppercase tracking-[0.2em] text-ink/75">{themeByKey(p.theme)?.title}</span>}
                  <span className="mt-1 block font-serif text-[16px] leading-snug text-ink lg:text-[18px]">{p.title}</span>
                  <span className="mt-1 block text-xs text-ink/70 lg:mt-2">{p.date}</span>
                </span>
                <ArrowRight aria-hidden className="size-4 self-end text-ink transition-transform group-hover:translate-x-1" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Author + books */}
      <section className="bg-secondary">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-7 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-0 lg:py-8">
          <div className="grid grid-cols-[96px_minmax(0,1fr)] items-center gap-4 sm:grid-cols-[180px_minmax(0,1fr)] lg:grid-cols-[156px_minmax(0,1fr)] xl:grid-cols-[168px_minmax(0,1fr)] lg:gap-6 lg:border-r lg:border-ink/15 lg:pr-7">
            <img src={portrait.url} alt="Arda Öztaşkın" loading="lazy" width={768} height={960} className="aspect-[4/5] w-full object-cover object-[center_12%] grayscale sm:aspect-[3/4] sm:object-[center_20%] lg:aspect-[4/5] lg:object-center" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-ink/75">Yazar</p>
              <h2 className="mt-2 font-serif text-2xl font-medium lg:text-[36px]">Arda Öztaşkın</h2>
              <p className="mt-3 hidden max-w-md font-serif text-[17px] leading-snug text-ink/80 sm:block">
                İnsan, teknoloji, ekonomi ve gezegenin kesişiminde yazıyor. Daha iyi bir yarın için daha derin düşünmenin peşinde.
              </p>
              <Link to="/yazar" className="mt-3 inline-flex min-h-11 items-center gap-2 font-serif text-[15px] text-ink hover:text-clay">
                Hakkında <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
          <div className="min-w-0 lg:pl-7">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-serif text-2xl font-medium lg:text-[28px]">Kitaplar</h2>
              <Link to="/kitaplar" className="inline-flex min-h-11 items-center gap-2 font-serif text-sm text-ink hover:text-clay">
                Tüm kitaplar <ArrowRight className="size-4" />
              </Link>
            </div>
            <ul aria-label="Kitaplar" tabIndex={0} className="mt-3 flex snap-x snap-mandatory gap-4 overflow-x-auto overscroll-x-contain pb-3 focus-visible:outline focus-visible:outline-1 focus-visible:outline-ink sm:grid sm:grid-cols-4 sm:gap-3 sm:overflow-visible sm:pb-0">
              {BOOKS.map((b) => (
                <li key={b.title} className="w-[calc((100%-1rem)/2)] min-w-0 shrink-0 snap-start sm:w-auto">
                  <Link to="/kitaplar" className="group block">
                    <img src={b.cover} alt={b.title} loading="lazy" width={240} height={340} className="aspect-[2/3] w-full object-cover shadow-sm transition-transform group-hover:-translate-y-1" />
                    <span className="mt-2 block text-xs tabular-nums text-ink/70">{b.year}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      <DemoNewsletter />
    </div>
  );
}

/** DESIGN-ONLY newsletter form: not connected to any service, stores nothing. */
export function DemoNewsletter() {
  const noteId = useId();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "already" | "err">("idle");
  const msg = {
    idle: "İstediğiniz zaman tek tıkla abonelikten çıkabilirsiniz.",
    sending: "Gönderiliyor…",
    ok: "Teşekkürler, aboneliğiniz alındı.",
    already: "Bu adres zaten kayıtlı.",
    err: "Geçerli bir e-posta adresi girin ve tekrar deneyin.",
  }[status];
  return (
    <section aria-labelledby="bulten-baslik" className="border-t border-ink/10">
      <div className="mx-auto grid max-w-7xl gap-4 px-5 py-8 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-10 lg:py-12">
        <div>
          <h2 id="bulten-baslik" className="font-serif text-2xl font-medium lg:text-[28px]">Yeni yazılardan haberdar olun</h2>
          <p className="mt-2 font-serif text-[16px] leading-snug text-ink/80">
            Akıllı Sürdürülebilirlik'teki yeni yazılar e-posta kutunuza gelsin.
          </p>
        </div>
        <div>
          <form
            noValidate
            onSubmit={async (e) => {
              e.preventDefault();
              const value = email.trim().toLowerCase();
              if (status === "sending") return;
              if (!value || value.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return setStatus("err");
              setStatus("sending");
              try {
                const res = await fetch("/api/public/newsletter-subscribe", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: value, lang: "tr", website, consent: true }),
                });
                if (!res.ok) return setStatus(res.status === 409 ? "already" : "err");
                setStatus("ok");
            void trackNewsletterSignup();
                setEmail("");
              } catch {
                setStatus("err");
              }
            }}
            className="flex flex-col gap-3 sm:flex-row"
            aria-describedby={noteId}
          >
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} aria-hidden="true" className="hidden" />
            <label htmlFor="home-email" className="sr-only">E-posta adresi</label>
            <input
              id="home-email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-posta adresiniz"
              className="min-h-11 min-w-0 flex-1 border-b border-ink/40 bg-transparent px-1 font-serif text-[16px] text-ink placeholder:text-ink/60 focus:border-ink focus:outline-none"
            />
            <Button type="submit" disabled={status === "sending"} className="h-11 rounded-none min-h-11 shrink-0 bg-ink px-6 text-[11px] uppercase tracking-[0.2em] text-paper hover:opacity-90">
              Abone ol
            </Button>
          </form>
          <p id={noteId} role="status" className="mt-3 text-xs leading-relaxed text-ink/75">{msg}</p>
        </div>
      </div>
    </section>
  );
}

